/* Isolated accessories collection interactions. */
(() => {
  const normalize = (value) => (value || "").trim().toLowerCase();
  const urlGroups = [
    { group: "model", param: "product", reveal: true },
    { group: "type", param: "type", reveal: false },
  ];

  function init(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const productGrid = root.querySelector("[data-product-grid]");
    let cards = [...root.querySelectorAll("[data-accessory-product]")];
    const filterOptions = [...root.querySelectorAll("[data-filter-option]")];
    const filterAllControls = [...root.querySelectorAll("[data-filter-all]")];
    const filterPanel = root.querySelector("[data-filter-panel]");
    const filterViews = [...root.querySelectorAll("[data-filter-view]")];
    const filterSections = [...root.querySelectorAll("[data-filter-section]")];
    const pagination = root.querySelector("[data-product-pagination]");
    const paginationPages = root.querySelector("[data-pagination-pages]");
    const paginationPrev = root.querySelector("[data-pagination-prev]");
    const paginationNext = root.querySelector("[data-pagination-next]");
    const emptyState = root.querySelector("[data-filter-empty]");
    let currentPage = 1;
    let isLoadingAllProducts = Boolean(productGrid?.dataset.nextPageUrl);
    let isApplyingUrlFilters = false;

    if (filterPanel && window.matchMedia("(max-width: 767px)").matches) {
      filterPanel.setAttribute("aria-hidden", "true");
    }

    function prepareCard(card) {
      card._tags = (card.dataset.productTags || "").split("||").map(normalize);
      card._compatibleModels = (card.dataset.compatibleModels || "")
        .replace(/\|\|/g, ";")
        .split(";")
        .map(normalize)
        .filter(Boolean);
    }

    cards.forEach(prepareCard);

    function getPageSize() {
      return window.matchMedia("(max-width: 767px)").matches
        ? Number(root.dataset.pageSizeMobile || 9)
        : Number(root.dataset.pageSizeDesktop || 9);
    }

    function sortCardsByAvailability() {
      if (!productGrid) return;
      cards.sort(
        (first, second) =>
          Number(first.classList.contains("is-sold-out")) - Number(second.classList.contains("is-sold-out")),
      );
      productGrid.append(...cards);
    }

    function renderPagination(totalPages) {
      if (!pagination || !paginationPages || !paginationPrev || !paginationNext) return;

      pagination.hidden = isLoadingAllProducts || totalPages <= 1;
      paginationPrev.disabled = currentPage <= 1;
      paginationNext.disabled = currentPage >= totalPages;
      paginationPages.replaceChildren();
      if (pagination.hidden) return;

      const visiblePages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
      if (currentPage <= 4) [2, 3, 4, 5].forEach((page) => visiblePages.add(page));
      if (currentPage >= totalPages - 3) {
        [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1].forEach((page) => visiblePages.add(page));
      }

      const pages = [...visiblePages].filter((page) => page > 0 && page <= totalPages).sort((a, b) => a - b);
      let previousPage = 0;

      pages.forEach((page) => {
        if (page - previousPage > 1) {
          const ellipsis = document.createElement("span");
          ellipsis.className = "accessories-pagination__ellipsis";
          ellipsis.textContent = "\u2026";
          ellipsis.setAttribute("aria-hidden", "true");
          paginationPages.append(ellipsis);
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = "accessories-pagination__page";
        button.dataset.page = String(page);
        button.textContent = String(page);
        button.setAttribute("aria-label", `Go to page ${page}`);
        if (page === currentPage) {
          button.classList.add("is-active");
          button.setAttribute("aria-current", "page");
        }
        paginationPages.append(button);
        previousPage = page;
      });
    }

    async function loadAllProducts() {
      if (!productGrid?.dataset.nextPageUrl) {
        isLoadingAllProducts = false;
        sortCardsByAvailability();
        render();
        return;
      }

      const seenProductIds = new Set(cards.map((card) => card.dataset.productId).filter(Boolean));
      const seenPageUrls = new Set();
      let nextPageUrl = productGrid.dataset.nextPageUrl;
      let loadFailed = false;

      root.dataset.productLoadState = "loading";
      productGrid.setAttribute("aria-busy", "true");

      try {
        while (nextPageUrl && root.isConnected) {
          const pageUrl = new URL(nextPageUrl, window.location.origin);
          const pageKey = pageUrl.href;
          if (seenPageUrls.has(pageKey)) break;
          seenPageUrls.add(pageKey);

          if (root.dataset.sectionId) pageUrl.searchParams.set("section_id", root.dataset.sectionId);

          const response = await fetch(pageUrl.href, { credentials: "same-origin" });
          if (!response.ok) throw new Error(`Unable to load accessories page: ${response.status}`);

          const html = await response.text();
          const fetchedDocument = new DOMParser().parseFromString(html, "text/html");
          const fetchedRoot = fetchedDocument.querySelector("[data-accessories-collection]");
          const fetchedGrid = fetchedRoot?.querySelector("[data-product-grid]");
          if (!fetchedGrid) throw new Error("Accessories grid was not found in the paginated response.");

          fetchedGrid.querySelectorAll("[data-accessory-product]").forEach((card) => {
            const productId = card.dataset.productId;
            if (productId && seenProductIds.has(productId)) return;
            if (productId) seenProductIds.add(productId);

            prepareCard(card);
            card.hidden = true;
            productGrid.append(card);
            cards.push(card);
          });

          nextPageUrl = fetchedGrid.dataset.nextPageUrl || "";
        }
      } catch (error) {
        loadFailed = true;
        console.error("Failed to load all accessory products.", error);
      } finally {
        isLoadingAllProducts = false;
        root.dataset.productLoadState = loadFailed ? "error" : "complete";
        productGrid.removeAttribute("aria-busy");
        sortCardsByAvailability();
        render();
      }
    }

    function selectedByGroup() {
      const groups = new Map();
      filterOptions
        .filter((input) => input.checked)
        .forEach((input) => {
          const group = normalize(input.dataset.filterGroup);
          if (!groups.has(group)) groups.set(group, []);
          groups.get(group).push(normalize(input.value));
        });
      return groups;
    }

    function optionLabel(input) {
      return input.dataset.filterLabel || input.value.split(":").pop() || input.value;
    }

    function sortModelFilterOptions() {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
      const modelInputs = filterOptions.filter((input) => normalize(input.dataset.filterGroup) === "model");
      const containers = new Set(
        modelInputs.map((input) => input.closest(".accessories-filter__options")).filter(Boolean),
      );

      containers.forEach((container) => {
        const optionRows = modelInputs
          .filter((input) => input.closest(".accessories-filter__options") === container)
          .map((input) => ({ input, row: input.closest(".accessories-filter__option") }))
          .filter(({ row }) => row);

        optionRows.sort((first, second) => {
          const firstLabel = optionLabel(first.input);
          const secondLabel = optionLabel(second.input);
          const firstMatch = firstLabel.match(/\d+/);
          const secondMatch = secondLabel.match(/\d+/);
          const firstNumber = firstMatch ? Number(firstMatch[0]) : -1;
          const secondNumber = secondMatch ? Number(secondMatch[0]) : -1;

          if (firstNumber !== secondNumber) return secondNumber - firstNumber;
          return collator.compare(firstLabel, secondLabel);
        });

        optionRows.forEach(({ row }) => container.append(row));
      });
    }
    function selectedLabels(group) {

      return filterOptions
        .filter((input) => input.checked && normalize(input.dataset.filterGroup) === group)
        .map(optionLabel)
        .filter(Boolean);
    }

    function syncUrlParams() {
      if (isApplyingUrlFilters) return;
      const url = new URL(window.location.href);
      urlGroups.forEach(({ group, param }) => {
        const labels = selectedLabels(group);
        if (labels.length) {
          url.searchParams.set(param, labels.join(", "));
        } else {
          url.searchParams.delete(param);
        }
      });
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }

    function applyUrlParams() {
      const params = new URLSearchParams(window.location.search);
      let activeView = null;
      let changed = false;

      isApplyingUrlFilters = true;
      urlGroups.forEach(({ group, param }) => {
        const rawValue = params.get(param);
        if (!rawValue) return;
        const selectedValues = rawValue.split(",").map(normalize).filter(Boolean);
        if (!selectedValues.length) return;

        filterOptions.forEach((input) => {
          if (normalize(input.dataset.filterGroup) !== group) return;
          const label = normalize(optionLabel(input));
          const value = normalize(input.value);
          const tagLabel = normalize(input.value.split(":").pop() || input.value);
          const shouldCheck = selectedValues.includes(label) || selectedValues.includes(value) || selectedValues.includes(tagLabel);
          input.checked = shouldCheck;
          changed = changed || shouldCheck;
        });

        activeView = activeView || group;
      });
      isApplyingUrlFilters = false;

      if (changed) {
        revealSelectedModelGroups();
        if (activeView) showFilterView(activeView);
      }
    }

    function revealSelectedModelGroups() {
      filterOptions
        .filter((input) => input.checked && normalize(input.dataset.filterGroup) === "model")
        .forEach((input) => {
          input.closest("details")?.setAttribute("open", "");
          input.closest(".accessories-filter__group")?.setAttribute("open", "");
          input.closest(".accessories-all-filter__series")?.setAttribute("open", "");
        });
    }

    function cardMatches(card, groups) {
      for (const [group, selectedTags] of groups.entries()) {
        if (group === "model") {
          const hasModelMatch = selectedTags.some((tag) => {
            const modelLabel = normalize(tag.split(":").pop() || tag);
            return card._tags.includes(tag) || card._compatibleModels.includes(modelLabel);
          });
          if (!hasModelMatch) return false;
          continue;
        }

        if (!selectedTags.some((tag) => card._tags.includes(tag))) return false;
      }
      return true;
    }

    function syncAllControls(groups) {
      filterAllControls.forEach((control) => {
        control.checked = !groups.has(normalize(control.dataset.filterAll));
      });
    }

    function render({ resetPage = false } = {}) {
      if (resetPage) currentPage = 1;
      const groups = selectedByGroup();
      const matching = cards.filter((card) => cardMatches(card, groups));
      const pageSize = getPageSize();
      const totalPages = Math.max(1, Math.ceil(matching.length / pageSize));
      currentPage = Math.min(Math.max(currentPage, 1), totalPages);
      const pageStart = (currentPage - 1) * pageSize;

      cards.forEach((card) => {
        card.hidden = true;
        card.classList.remove("is-filtered-in");
      });
      matching.slice(pageStart, pageStart + pageSize).forEach((card) => {
        card.hidden = false;
        card.classList.add("is-filtered-in");
      });

      syncAllControls(groups);
      if (emptyState) emptyState.hidden = isLoadingAllProducts || matching.length !== 0;
      renderPagination(totalPages);
      syncUrlParams();
    }

    function goToPage(page) {
      currentPage = page;
      render();
      productGrid?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function resetFilters() {
      filterOptions.forEach((input) => (input.checked = false));
      render({ resetPage: true });
    }

    function openFilter() {
      if (!filterPanel) return;
      root.classList.add("is-filter-open");
      filterPanel.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("accessories-lock-scroll");
    }

    function closeFilter() {
      root.classList.remove("is-filter-open");
      filterPanel?.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("accessories-lock-scroll");
    }

    function showFilterView(view) {
      filterViews.forEach((button) => {
        button.classList.toggle("is-active", button.dataset.filterView === view);
      });
      filterSections.forEach((section) => {
        section.classList.toggle("is-mobile-active", section.dataset.filterSection === view);
      });
    }

    filterOptions.forEach((input) => {
      input.addEventListener("change", () => render({ resetPage: true }));
    });
    filterAllControls.forEach((control) => {
      control.addEventListener("change", () => {
        const group = normalize(control.dataset.filterAll);
        filterOptions.forEach((input) => {
          if (normalize(input.dataset.filterGroup) === group) input.checked = false;
        });
        render({ resetPage: true });
      });
    });
    filterViews.forEach((button) => {
      button.addEventListener("click", () => showFilterView(button.dataset.filterView));
    });
    root.querySelectorAll("[data-filter-reset]").forEach((button) => button.addEventListener("click", resetFilters));
    root.querySelector("[data-filter-open]")?.addEventListener("click", openFilter);
    root.querySelector("[data-filter-close]")?.addEventListener("click", closeFilter);
    root.querySelector("[data-filter-done]")?.addEventListener("click", closeFilter);
    paginationPages?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-page]");
      if (button) goToPage(Number(button.dataset.page));
    });
    paginationPrev?.addEventListener("click", () => {
      if (currentPage > 1) goToPage(currentPage - 1);
    });
    paginationNext?.addEventListener("click", () => {
      const groups = selectedByGroup();
      const pageSize = getPageSize();
      const totalPages = Math.max(1, Math.ceil(cards.filter((card) => cardMatches(card, groups)).length / pageSize));
      if (currentPage < totalPages) goToPage(currentPage + 1);
    });

    window.addEventListener("resize", () => {
      if (!window.matchMedia("(max-width: 767px)").matches) closeFilter();
      render();
    });
    sortModelFilterOptions();
    applyUrlParams();
    render({ resetPage: true });
    loadAllProducts();
  }

  document.querySelectorAll("[data-accessories-collection]").forEach(init);
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-accessories-collection]").forEach(init);
  });
})();